import React, { useState, useEffect, useCallback } from 'react';
import api, { apiService } from '../services/api';
import { SiteUser, SiteToken } from '../types';
import { validateConfig } from '../utils/configSchema';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../components/DataTable';
import AutocompleteSelect from '../components/AutocompleteSelect';

const SiteUsers: React.FC = () => {
  const [users, setUsers] = useState<SiteUser[]>([]);
  const [siteTokens, setSiteTokens] = useState<SiteToken[]>([]);
  const [selectedSiteToken, setSelectedSiteToken] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Config version management states
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SiteUser | null>(null);
  const [configVersions, setConfigVersions] = useState<any[]>([]);
  const [configJson, setConfigJson] = useState('');
  const [impressaConfigJson, setImpressaConfigJson] = useState('');
  const [uiConfigJson, setUiConfigJson] = useState('');
  const [versionLoading, setVersionLoading] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState<number | null>(null);

  // Detail view states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchSiteTokens();
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAuthSiteUsers(selectedSiteToken);
      setUsers(data);
    } catch {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [selectedSiteToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchSiteTokens = async () => {
    try {
      const data = await apiService.getSiteTokens();
      setSiteTokens(data);
    } catch {
      // non-critical, filter will just be empty
    }
  };

  const refreshVersions = async (user: SiteUser) => {
    try {
      const response = await apiService.getUserConfigHistory(user.id);
      const sorted = response.history.sort((a: any, b: any) => {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setConfigVersions(sorted);
    } catch (err) {
      setError('Failed to load config versions');
    }
  };

  const openVersionModal = async (user: SiteUser) => {
    setSelectedUser(user);
    setVersionLoading(true);
    setShowVersionModal(true);
    try {
      const response = await apiService.getUserConfigHistory(user.id);
      const sorted = response.history.sort((a: any, b: any) => {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setConfigVersions(sorted);
    } catch (err) {
      setError('Failed to load config versions');
    } finally {
      setVersionLoading(false);
    }
  };

  const openAddVersionModal = (existingConfig?: any, existingImpressaConfig?: any, existingUiConfig?: any, versionId?: number) => {
    setConfigJson(JSON.stringify(existingConfig || {}, null, 2));
    setImpressaConfigJson(JSON.stringify(existingImpressaConfig || {}, null, 2));
    setUiConfigJson(JSON.stringify(existingUiConfig || {}, null, 2));
    setEditingVersionId(versionId || null);
    setShowAddVersionModal(true);
  };

  const handleAddVersion = async (setActive: boolean) => {
    if (!selectedUser) return;

    let config: any, impressaConfig: any, uiConfig: any;
    try {
      config = JSON.parse(configJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in Config: ${err.message}`);
      return;
    }
    try {
      impressaConfig = JSON.parse(impressaConfigJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in Impressa Config: ${err.message}`);
      return;
    }
    try {
      uiConfig = JSON.parse(uiConfigJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in UI Config: ${err.message}`);
      return;
    }

    if (Object.keys(config).length > 0) {
      const validationError = validateConfig(config);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    try {
      if (editingVersionId) {
        await apiService.updateUserConfig(selectedUser.id, editingVersionId, config, setActive);
        await apiService.updateUserImpressaConfig(selectedUser.id, editingVersionId, impressaConfig, setActive);
        await apiService.updateUserUIConfig(selectedUser.id, editingVersionId, uiConfig, setActive);
      } else {
        await apiService.createUserConfigVersion(selectedUser.id, config, impressaConfig, uiConfig, setActive);
      }

      setShowAddVersionModal(false);
      setError(null);
      await refreshVersions(selectedUser);
      await fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || (editingVersionId ? 'Failed to update version' : 'Failed to create version'));
    }
  };

  const handleActivateVersion = async (versionId: number) => {
    if (!selectedUser) return;
    try {
      const version = configVersions.find(v => v.id === versionId);
      if (!version) return;
      await api.patch(`/auth/users/${selectedUser.id}/config/activate/`, {
        version: version.version
      });
      await refreshVersions(selectedUser);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate version');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };


  const openDetailModal = async (user: SiteUser) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const detail = await apiService.getUserDetail(user.id);
      setUserDetail(detail);
    } catch (err: any) {
      if (err.response?.data) {
        setUserDetail(err.response.data);
      } else {
        setError('Failed to load user details');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: Column<SiteUser>[] = [
    {
      key: 'username',
      header: 'Username',
      skeletonWidth: 'w-32',
      render: (user) => (
        <span className="text-sm font-bold text-gray-900">{user.username}</span>
      ),
    },
    {
      key: 'site_token',
      header: 'Site Token',
      skeletonWidth: 'w-36',
      render: (user) => (
        <code className="font-mono text-[11px] bg-slate-100 px-2 py-1 rounded-md text-gray-700">
          {user.site_token?.token ?? '-'}
        </code>
      ),
    },
    {
      key: 'site',
      header: 'Site',
      skeletonWidth: 'w-28',
      render: (user) => (
        <span
          className="text-sm text-gray-700 truncate block max-w-[180px]"
          title={user.site_token?.workspace_name || '-'}
        >
          {user.site_token?.workspace_name || '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      skeletonWidth: 'w-24',
      render: (user) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => openDetailModal(user)}
            title="View details"
            className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => openVersionModal(user)}
            title="Configure"
            className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="mb-6">
        <h1 className="m-0 mb-2 text-3xl font-semibold">Users</h1>
        <p className="text-gray-600 m-0">
          Manage site users, their configurations, and approval status
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-5 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="bg-transparent border-0 text-red-600 cursor-pointer">Dismiss</button>
        </div>
      )}

      <div className="mb-5 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-500 whitespace-nowrap">Filter by Site:</label>
        <AutocompleteSelect
          options={siteTokens.map(t => ({ value: t.id, label: t.workspace_name || t.name || String(t.id) }))}
          value={selectedSiteToken}
          onChange={(v) => setSelectedSiteToken(v !== undefined ? Number(v) : undefined)}
          placeholder="Search sites…"
          allLabel="All Sites"
        />
      </div>

      <DataTable
        columns={columns}
        data={users}
        rowKey={(u) => u.id}
        loading={loading}
        skeletonRows={6}
        emptyMessage={
          <div>
            <p>No users found.</p>
            <p className="text-xs text-slate-400 mt-1">Users are synced from the platform via Site Tokens.</p>
          </div>
        }
      />

      {/* Config Version Management Modal */}
      {showVersionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={() => setShowVersionModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">Config Versions - {selectedUser.username}</h3>
              <button onClick={() => setShowVersionModal(false)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800">&times;</button>
            </div>

            <div className="mb-4 flex justify-end">
              <button
                onClick={() => openAddVersionModal()}
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors"
              >
                + Add New Version
              </button>
            </div>

            {versionLoading ? (
              <p className="text-gray-500 text-center py-5">Loading versions...</p>
            ) : configVersions.length === 0 ? (
              <p className="text-gray-500 text-center py-5">No config versions found.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {configVersions.map((version) => (
                  <div key={version.id} className={`border-2 rounded-lg p-4 ${version.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg font-semibold text-gray-800 m-0">Version {version.version}</h4>
                        {version.is_active && (
                          <span className="bg-green-600 text-white text-xs px-2 py-1 rounded font-semibold">ACTIVE</span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">{formatDate(version.created_at)}</div>
                        <div className="text-xs text-gray-500">by {version.created_by || 'System'}</div>
                      </div>
                    </div>
                    <div className="mb-3 flex flex-col gap-3">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Config</div>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.config, null, 2)}</pre>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Impressa Config</div>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.impressa_config, null, 2)}</pre>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">UI Config</div>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.ui_config, null, 2)}</pre>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openAddVersionModal(version.config, version.impressa_config, version.ui_config, version.version)}
                        className="bg-gray-600 hover:bg-gray-700 text-white py-1.5 px-4 rounded text-sm transition-colors"
                      >
                        Edit
                      </button>
                      {!version.is_active && (
                        <button
                          onClick={() => handleActivateVersion(version.id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-4 rounded text-sm transition-colors"
                        >
                          Set as Active
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add New Version Modal */}
      {showAddVersionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001]" onClick={() => setShowAddVersionModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">{editingVersionId ? 'Edit Config Version' : 'Add New Config Version'}</h3>
              <button onClick={() => setShowAddVersionModal(false)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800">&times;</button>
            </div>

            <div className="flex flex-col gap-5">
              <div>
                <label className="block mb-2 font-semibold text-gray-800 text-sm">Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6"
                  value={configJson}
                  onChange={(e) => setConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold text-gray-800 text-sm">Impressa Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6"
                  value={impressaConfigJson}
                  onChange={(e) => setImpressaConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold text-gray-800 text-sm">UI Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6"
                  value={uiConfigJson}
                  onChange={(e) => setUiConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <button onClick={() => setShowAddVersionModal(false)} className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-5 rounded text-sm transition-colors">Cancel</button>
                {editingVersionId ? (
                  <>
                    <button onClick={() => handleAddVersion(false)} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-5 rounded text-sm transition-colors">Save</button>
                    <button onClick={() => handleAddVersion(true)} className="bg-green-600 hover:bg-green-700 text-white py-2 px-5 rounded text-sm transition-colors">Save & Set Active</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleAddVersion(false)} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-5 rounded text-sm transition-colors">Save as Draft</button>
                    <button onClick={() => handleAddVersion(true)} className="bg-green-600 hover:bg-green-700 text-white py-2 px-5 rounded text-sm transition-colors">Save & Set Active</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">User Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800">&times;</button>
            </div>

            {detailLoading ? (
              <p className="text-gray-500 text-center py-5">Loading details...</p>
            ) : userDetail ? (
              userDetail.error ? (
                <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded">
                  <strong>Error:</strong> {userDetail.error}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Basic Info Card */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {userDetail.id && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">ID</div>
                          <div className="text-sm font-semibold text-gray-800">{userDetail.id}</div>
                        </div>
                      )}
                      {userDetail.username && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Username</div>
                          <div className="text-sm font-semibold text-gray-800">{userDetail.username}</div>
                        </div>
                      )}
                      {userDetail.email && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Email</div>
                          <div className="text-sm text-gray-700">{userDetail.email}</div>
                        </div>
                      )}
                      {userDetail.uuid && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">UUID</div>
                          <div className="text-sm text-gray-700 font-mono">{userDetail.uuid}</div>
                        </div>
                      )}
                      {userDetail.tool && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Tool</div>
                          <div className="text-sm text-gray-700">{userDetail.tool}</div>
                        </div>
                      )}
                      {userDetail.approved !== undefined && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase mb-1">Approval Status</div>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            userDetail.approved === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            userDetail.approved === 'REJECTED' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {userDetail.approved}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamps Card */}
                  {(userDetail.created_at || userDetail.updated_at || userDetail.last_login_attempt) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Timestamps</h4>
                      <div className="grid grid-cols-2 gap-4">
                        {userDetail.created_at && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase mb-1">Created At</div>
                            <div className="text-sm text-gray-700">{formatDate(userDetail.created_at)}</div>
                          </div>
                        )}
                        {userDetail.updated_at && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase mb-1">Updated At</div>
                            <div className="text-sm text-gray-700">{formatDate(userDetail.updated_at)}</div>
                          </div>
                        )}
                        {userDetail.last_login_attempt && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase mb-1">Last Login</div>
                            <div className="text-sm text-gray-700">{formatDate(userDetail.last_login_attempt)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Config Card */}
                  {userDetail.config && Object.keys(userDetail.config).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Configuration</h4>
                      <div className="bg-gray-50 rounded p-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700">{JSON.stringify(userDetail.config, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  {/* Additional Fields Card */}
                  {Object.keys(userDetail).filter(key =>
                    !['id', 'username', 'email', 'uuid', 'tool', 'approved', 'created_at', 'updated_at', 'last_login_attempt', 'config', 'error'].includes(key)
                  ).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Additional Information</h4>
                      <div className="bg-gray-50 rounded p-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700">
                          {JSON.stringify(
                            Object.fromEntries(
                              Object.entries(userDetail).filter(([key]) =>
                                !['id', 'username', 'email', 'uuid', 'tool', 'approved', 'created_at', 'updated_at', 'last_login_attempt', 'config', 'error'].includes(key)
                              )
                            ),
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )
            ) : (
              <p className="text-gray-500 text-center py-5">No details available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteUsers;
